import React, { useContext, useMemo } from 'react';
import { t } from '@sm/intl';
import { Typography } from '@wds/typography';
import { PricingExperienceContext } from '~pricing/lib/PricingExperience/PricingExperienceContext';
import { PACKAGES, COMPARISON_PACKAGES } from '~app/pages/Pricing/lib/constants';
import { PackageHeaderProps, PackageData, PackageType } from '~pricing/lib/summaryPackages';

import packageDefs from '~pricing/components/Summary/packageDefs';
import PackageCta from '~pricing/components/PackageCta';
import PackagePrice from '~pricing/components/PackageHeader/PackagePrice';
import PackageByline from '~pricing/components/PackageHeader/PackageByline';
import Ribbon from '~pricing/components/Ribbon';

import COPY from './copy';

import {
  calculateSavingsPercent,
  formatMonthlyCostInLocaleCurrency,
  formatCostInLocaleCurrency,
  getCoreCostFromSkus,
} from '~app/pages/Pricing/lib/lib-helpers';

import usePackageHeaderStyles from '~app/pages/Pricing/components/PackageHeader/useStyles';

export default function PackageHeader({
  ctaLabel,
  details,
  packageName,
  skuCosts,
  packageType,
}: PackageHeaderProps): React.ReactElement {
  const { Header } = usePackageHeaderStyles();
  const { displayName } = details;

  const comparisonPackageName = COMPARISON_PACKAGES[packageName];
  const coreSeatCost = useMemo(() => getCoreCostFromSkus(skuCosts), [skuCosts]);
  const formattedPrice = useMemo(
    () => coreSeatCost && formatCostInLocaleCurrency(coreSeatCost),
    [coreSeatCost]
  ) as string;
  const formattedMonthlyPrice = useMemo(
    () => coreSeatCost && formatMonthlyCostInLocaleCurrency(coreSeatCost),
    [coreSeatCost]
  ) as string;
  const packageData = useContext(PricingExperienceContext);
  let ribbonText = packageDefs[packageName]?.highlighted ? t(COPY.BEST_VALUE) : null;

  const comparisonPackage = useMemo(
    () =>
      comparisonPackageName
        ? packageData?.find((packageObj: PackageData) => packageObj.package === comparisonPackageName)
        : null,
    [comparisonPackageName, packageData]
  );
  const comparisonSeatCost = useMemo(
    () => (comparisonPackage ? getCoreCostFromSkus(comparisonPackage?.skuCost) : null),
    [comparisonPackage]
  );
  const savingsPercentage = useMemo(
    () => (coreSeatCost && comparisonSeatCost ? calculateSavingsPercent(comparisonSeatCost, coreSeatCost) : 0),
    [comparisonSeatCost, coreSeatCost]
  );

  if (savingsPercentage > 0) {
    ribbonText = t(COPY.SAVE_PERCENTAGE, { percentage: savingsPercentage });
  }

  const byline =
    packageType === PackageType.Teams
      ? packageDefs[packageName]?.byline
      : packageDefs[packageName]?.pricePerMonthAnnually?.(formattedPrice);

  return (
    <>
      {ribbonText && <Ribbon packageName={packageName}>{ribbonText}</Ribbon>}

      <header data-testid="PackageHeader__Header" className={Header}>
        <Typography component="h2" variant="sectionTitle" data-testid="PackageHeader__HeaderText" align="center">
          {displayName.toUpperCase()}
        </Typography>
        {packageName !== PACKAGES.Enterprise && (
          <PackagePrice
            packageName={packageName}
            formattedPrice={packageName === PACKAGES.StandardMonthly ? formattedPrice : formattedMonthlyPrice}
          />
        )}
        <PackageByline byline={byline} packageName={packageName} />
        <PackageCta ctaLabel={ctaLabel} packageName={packageName} />
      </header>
    </>
  );
}
